import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, siteInfo } from '@/mocks/home';
import { getArticle, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticle(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.relatedArticleIds) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground-700 mb-2">文章未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-block whitespace-nowrap px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://lymeijialaifu.cn' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://lymeijialaifu.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://lymeijialaifu.cn/article/${article.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.time,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    url: `https://lymeijialaifu.cn/article/${article.slug}`,
  };

  return (
    <>
      <title>{article.title} - {siteInfo.name}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`https://lymeijialaifu.cn/article/${article.slug}`} />
      <meta property="og:title" content={`${article.title} - ${siteInfo.name}`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://lymeijialaifu.cn/article/${article.slug}`} />
      <meta property="og:image" content={article.imageUrl} />
      <meta property="article:published_time" content={article.time} />
      <meta property="article:author" content={article.author} />
      <meta property="article:tag" content={article.tags.join(',')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - ${siteInfo.name}`} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.imageUrl} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-foreground-950 transition-colors cursor-pointer">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-500/90 text-background-950 text-xs font-semibold">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-foreground-400"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-foreground-400"></i>
                  {article.time}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-accent-500"></i>
                  {article.hotScore} 热度
                </span>
              </div>
            </header>

            <figure className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={`${article.title} - ${siteInfo.name}`}
              />
              <figcaption className="mt-2 text-xs text-foreground-400 text-center">
                {article.title}
              </figcaption>
            </figure>

            <div className="prose max-w-none">
              {article.content.map((block, idx) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={idx} className="text-foreground-700 leading-relaxed text-[15px] mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70 flex items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-background-200 transition-colors">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button className="whitespace-nowrap px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors flex items-center gap-1.5" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                  分享到微博
                </button>
                <button className="whitespace-nowrap px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm cursor-pointer hover:bg-background-200 transition-colors flex items-center gap-1.5" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                  分享到微信
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <span>声明：本文内容由本中心编辑团队整理发布，转载请注明出处。</span>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-12 md:py-16 mt-8" aria-label="相关推荐">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex items-center gap-3 mb-8">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-links-line"></i>
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group cursor-pointer hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-0.5 rounded-full bg-accent-500/90 text-background-950 text-xs font-semibold">
                            {related.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-foreground-950 font-semibold text-sm leading-snug line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h3>
                        <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                          {related.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}